import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditDetailsTab({ adminToken }) {
  const navigate = useNavigate();
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [audits, setAudits] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");

  const fetchAudits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-details`, { params: { page, limit: 20 }, headers: { Authorization: `Bearer ${adminToken}` } });
      setAudits(res.data.audits || []);
      setPagination({ total: res.data.total, pages: res.data.pages });
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [page, adminToken]);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  const handleSaveVideoUrl = async (placeId) => {
    try {
      await axios.put(`${API}/admin/audit/${placeId}/video-url`, { video_url: videoUrl }, { headers: getHeaders() });
      toast.success("Video URL updated"); setEditingVideo(null); setVideoUrl(""); fetchAudits();
    } catch { toast.error("Failed"); }
  };

  return (
    <Card className="bg-white border-[#E5E7EB]">
      <CardHeader><CardTitle className="text-lg text-[#111827]">Audit Details ({pagination.total})</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
              <TableHead className="text-[#6B7280]">Business</TableHead>
              <TableHead className="text-center text-[#6B7280]">Score</TableHead>
              <TableHead className="text-center text-[#6B7280]">Account</TableHead>
              <TableHead className="text-[#6B7280]">Email</TableHead>
              <TableHead className="text-center text-[#6B7280]">Paid</TableHead>
              <TableHead className="text-[#6B7280]">Video URL</TableHead>
              <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audits.length > 0 ? audits.map((a, i) => (
              <TableRow key={a.place_id || i} className="border-[#E5E7EB]">
                <TableCell><span className="font-medium text-sm text-[#111827]">{a.business_name}</span><p className="text-xs text-[#9CA3AF] truncate max-w-[180px]">{a.address}</p></TableCell>
                <TableCell className="text-center"><Badge className={a.score >= 70 ? "bg-emerald-50 text-emerald-600" : a.score >= 40 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}>{a.score}</Badge></TableCell>
                <TableCell className="text-center">{a.has_account ? <Badge className="bg-emerald-50 text-emerald-600">Yes</Badge> : <Badge className="bg-slate-50 text-slate-500">No</Badge>}</TableCell>
                <TableCell className="text-sm text-[#6B7280]">{a.account_email || "—"}</TableCell>
                <TableCell className="text-center">{a.has_paid_27 ? <Badge className="bg-emerald-50 text-emerald-600">Paid</Badge> : <Badge className="bg-slate-50 text-slate-500">Free</Badge>}</TableCell>
                <TableCell>
                  {editingVideo === a.place_id ? (
                    <div className="flex items-center gap-1">
                      <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." className="h-8 text-xs w-40 bg-white border-[#E5E7EB]" />
                      <Button size="sm" variant="ghost" className="h-8 text-emerald-600 px-2" onClick={() => handleSaveVideoUrl(a.place_id)}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-[#9CA3AF] px-2" onClick={() => { setEditingVideo(null); setVideoUrl(""); }}>X</Button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingVideo(a.place_id); setVideoUrl(a.video_url || ""); }}
                      className={`text-xs truncate max-w-[120px] block ${a.video_url ? "text-[#3B5BDB] underline" : "text-[#9CA3AF]"}`}>
                      {a.video_url ? a.video_url.substring(0, 25) + "..." : "Add video"}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => navigate(`/report/${a.place_id}`)} className="text-[#3B5BDB]"><ExternalLink className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#9CA3AF]">No audit details</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
